import { useAppDispatch, useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import StatusHeader from 'components/common/StatusHeader';
import { appActions } from 'features/common/appSlice';
import { selectIsEditMode } from 'features/common/editModeSlice';
import importDataService from 'features/ImportData/importDataService';
import { CounterPattern } from 'features/ImportData/models';
import { COUPON_CLASSIFICATIONS } from 'features/MenuList/constants';
import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { DATE_FORMAT_YYYY_MM_DD, displayError, exportToCsvFile, handleServiceError } from 'utils';
import CounterPatternMobileOrderDisplayModal, {
  MobileOrderDisplayModalProps,
} from '../components/CounterPatternMobileOrderDisplayModal';
import CounterPatternSettingFormSearch, { MENU_TYPE_DEFAULT } from '../components/CounterPatternSettingFormSearch';
import CounterPatternSettingTable from '../components/CounterPatternSettingTable';
import CounterPatternStoreMobileOrderDisplayModal, {
  CounterPatternStoreMobileOrderDisplayModalProps,
} from '../components/CounterPatternStoreMobileOrderDisplayModal';
import counterPatternService from '../counterPatternService';
import { CounterPatternMobileOrderDisplayPayload, MenuCounterPattern, MenuDetailCounterPattern } from '../models';
import './CounterPatternSettingPage.scss';

export enum SortType {
  ASC = 'ASC',
  DESC = 'DESC',
}

export enum Columns {
  MENU_CLASSIFICATION_ID = 'menu_classification_id',
  CD = 'cd',
  NAME = 'name',
  SUB_FLG = 'sub_flg',
  START_DATE = 'start_date',
  END_DATE = 'end_date',
}

function CounterPatternSettingPage() {
  const { t } = useTranslation();
  // const history = useHistory();
  const { showGlobalModal } = useGlobalModal();
  const dispatch = useAppDispatch();

  // refs
  const isExportRef = useRef<boolean>(false);

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  // state
  const [formSearchValue, setFormSearchValue] = useState<CounterPatternMobileOrderDisplayPayload>({
    menuClassification: 0,
    menuType: MENU_TYPE_DEFAULT,
    menuName: '',
    menuCode: '',
    dataDate: new Date(),
    posCode: '',
  });
  const [counterPatternSource, setCounterPatternSource] = useState<CounterPattern[]>([]);
  const [menuCounterPatternList, setMenuCounterPatternList] = useState<MenuCounterPattern[]>([]);
  const [showCounterPatternMobileOrderDisplayModal, setShowCounterPatternMobileOrderDisplayModal] = useState(false);
  const [selectedMobileOrderDisplayModalData, setSelectedMobileOrderDisplayModalData] =
    useState<MobileOrderDisplayModalProps>();
  const [showCounterPatternStoreMobileOrderDisplayModal, setShowCounterPatternStoreMobileOrderDisplayModal] =
    useState(false);
  const [selectedStoreMobileOrderDisplayModalData, setSelectedStoreMobileOrderDisplayModalData] =
    useState<CounterPatternStoreMobileOrderDisplayModalProps>();

  // sort state
  const [sortedData, setSortedData] = useState<MenuCounterPattern[]>([]);
  const [sortBy, setSortBy] = useState<{ sortName: string; sortType: SortType | null }>({
    sortName: Columns.START_DATE,
    sortType: SortType.DESC,
  });

  async function loadCounterPatternSource() {
    try {
      const response = await importDataService.get();
      setCounterPatternSource(response.data);
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }
  }

  async function loadMenuCounterPatternList() {
    try {
      const response = await counterPatternService.getMenuCounterPatternList(formSearchValue);
      setMenuCounterPatternList(response.data);
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }
  }

  async function loadInitData() {
    await loadCounterPatternSource();
    await loadMenuCounterPatternList();
  }

  // effect
  useEffect(() => {
    // load init data
    isExportRef.current = false;
    loadInitData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleSearch(formSearchValue: CounterPatternMobileOrderDisplayPayload) {
    isExportRef.current = false;
    setSortBy({
      sortName: Columns.START_DATE,
      sortType: SortType.DESC,
    });
    setFormSearchValue(formSearchValue);
  }

  function handleExportCSV(formSearchValue: CounterPatternMobileOrderDisplayPayload) {
    isExportRef.current = true;
    setFormSearchValue(formSearchValue);
  }

  useEffect(() => {
    loadMenuCounterPatternList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formSearchValue]);

  const handleShowCounterPatternMobileOrderDisplayModal = useCallback(
    (
      menu: MenuCounterPattern,
      menuDetail: MenuDetailCounterPattern,
      counterPattern: CounterPattern,
      mobileOrderDisplayFlg: boolean
    ) => {
      const mobileOrderDisplayModalData: MobileOrderDisplayModalProps = {
        posCodeId: menuDetail.pos_code_id,
        posCode: menuDetail.pos_code_cd,
        posName: menuDetail.name,
        counterPattern: counterPattern,
        menuName: menu.name,
        menuCode: menu.cd,
        couponClassification: menu.coupon_classification,
        defaultMobileOrderDisplayFlg: mobileOrderDisplayFlg,
      };
      setSelectedMobileOrderDisplayModalData(mobileOrderDisplayModalData);
      setShowCounterPatternMobileOrderDisplayModal(true);
    },
    []
  );

  const handleCloseCounterPatternMobileOrderDisplayModal = useCallback(
    function (isDataChanged: boolean = false) {
      setShowCounterPatternMobileOrderDisplayModal(false);
      setSelectedMobileOrderDisplayModalData(undefined);

      if (isDataChanged) {
        loadMenuCounterPatternList();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [menuCounterPatternList]
  );

  const handleShowCounterPatternStoreMobileOrderDisplayModal = useCallback(
    (menu: MenuCounterPattern, menuDetail: MenuDetailCounterPattern) => {
      const storeMobileOrderDisplayModalData: CounterPatternStoreMobileOrderDisplayModalProps = {
        posCodeId: menuDetail.pos_code_id,
        posCode: menuDetail.pos_code_cd,
        posName: menuDetail.name,
        menuName: menu.name,
        menuCode: menu.cd,
        couponClassification: menu.coupon_classification,
      };
      setSelectedStoreMobileOrderDisplayModalData(storeMobileOrderDisplayModalData);
      setShowCounterPatternStoreMobileOrderDisplayModal(true);
    },
    []
  );

  const handleCloseCounterPatternStoreMobileOrderDisplayModal = useCallback(
    function (isDataChanged: boolean = false) {
      setShowCounterPatternStoreMobileOrderDisplayModal(false);
      setSelectedStoreMobileOrderDisplayModalData(undefined);

      if (isDataChanged) {
        loadMenuCounterPatternList();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [menuCounterPatternList]
  );

  function sortData() {
    if (sortBy.sortType === null) {
      return [...menuCounterPatternList];
    }

    return [...menuCounterPatternList].sort((a, b) => {
      if (sortBy.sortName === Columns.MENU_CLASSIFICATION_ID) {
        if (sortBy.sortType === SortType.ASC) {
          return a.menu_classification_id - b.menu_classification_id;
        } else if (sortBy.sortType === SortType.DESC) {
          return b.menu_classification_id - a.menu_classification_id;
        }
      }

      if (sortBy.sortName === Columns.CD) {
        if (sortBy.sortType === SortType.ASC) {
          return (a.cd + a.coupon_classification.toString()).localeCompare(b.cd + b.coupon_classification.toString());
        } else if (sortBy.sortType === SortType.DESC) {
          return (b.cd + b.coupon_classification.toString()).localeCompare(a.cd + a.coupon_classification.toString());
        }
      }

      if (sortBy.sortName === Columns.NAME) {
        if (sortBy.sortType === SortType.ASC) {
          return a.name.localeCompare(b.name, 'ja');
        } else if (sortBy.sortType === SortType.DESC) {
          return b.name.localeCompare(a.name, 'ja');
        }
      }

      if (sortBy.sortName === Columns.SUB_FLG) {
        if (sortBy.sortType === SortType.ASC) {
          return Number(a.sub_flg) - Number(b.sub_flg);
        } else if (sortBy.sortType === SortType.DESC) {
          return Number(b.sub_flg) - Number(a.sub_flg);
        }
      }

      if (sortBy.sortName === Columns.START_DATE) {
        if (sortBy.sortType === SortType.ASC) {
          return a.start_date.localeCompare(b.start_date);
        } else if (sortBy.sortType === SortType.DESC) {
          return b.start_date.localeCompare(a.start_date);
        }
      }

      if (sortBy.sortName === Columns.END_DATE) {
        if (sortBy.sortType === SortType.ASC) {
          return a.end_date.localeCompare(b.end_date);
        } else if (sortBy.sortType === SortType.DESC) {
          return b.end_date.localeCompare(a.end_date);
        }
      }

      return 0;
    });
  }

  function handleSortByCol(col: Columns) {
    if (sortBy.sortName !== col) {
      setSortBy({
        sortName: col,
        sortType: SortType.ASC,
      });
    } else {
      let sortType: SortType | null = null;
      if (sortBy.sortType === SortType.ASC) {
        sortType = SortType.DESC;
      } else if (sortBy.sortType === SortType.DESC) {
        sortType = null;
      } else {
        sortType = SortType.ASC;
      }
      setSortBy({
        sortName: col,
        sortType: sortType,
      });
    }
  }

  // effects
  useEffect(() => {
    const sorted = sortData();
    setSortedData(sorted);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuCounterPatternList]);

  useEffect(() => {
    const sorted = sortData();
    setSortedData(sorted);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy]);

  useEffect(() => {
    if (isExportRef.current) {
      isExportRef.current = false;
      processExportCSV(sortedData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedData]);

  function processExportCSV(dataForExport: MenuCounterPattern[]) {
    dispatch(appActions.showAppLoading());

    const csvDataList: any[] = [];
    dataForExport.forEach((item) => {
      const menuDetails = item.menu_details;

      menuDetails.forEach((menuDetail) => {
        const couponName = COUPON_CLASSIFICATIONS[item.coupon_classification]
          ? `${COUPON_CLASSIFICATIONS[item.coupon_classification]}`
          : '';

        const csvData = [
          item.menu_classification_name,
          `${item.cd}${couponName}`,
          item.name,
          item.sub_flg ? '〇' : 'ー',
          moment(item.start_date).format(DATE_FORMAT_YYYY_MM_DD),
          moment(item.end_date).format(DATE_FORMAT_YYYY_MM_DD),
          `pos_cd:${menuDetail.pos_code_cd}`,
          menuDetail.name,
        ];

        counterPatternSource.forEach((counterPattern) => {
          const moDisplayInfo = menuDetail.mo_display_by_counter_pattern?.[counterPattern.id.toString()];

          const isSettingByUser = moDisplayInfo?.is_change_from_restaurant || false;

          if (!moDisplayInfo) {
            csvData.push(t('StoreSettingDetailPage.not_apply'));
            csvData.push('ー');
          } else {
            csvData.push(
              moDisplayInfo.mobile_order_display_flg
                ? t('StoreSettingDetailPage.enable')
                : t('StoreSettingDetailPage.disable')
            );
            csvData.push(isSettingByUser ? '〇' : 'ー');
          }
        });

        csvDataList.push(csvData);
      });
    });
    const filename = `regipattern_export_${moment().format('YYYYMMDD')}.csv`;
    const columns: string[] = [
      t('CounterPatternSettingPage.menu_classification'),
      t('CounterPatternSettingPage.menu_code'),
      t('CounterPatternSettingPage.menu_name'),
      t('CounterPatternSettingPage.menu_type'),
      t('CounterPatternSettingPage.start_date'),
      t('CounterPatternSettingPage.end_date'),
      t('CounterPatternSettingPage.pos_code'),
      t('CounterPatternSettingPage.pos_name'),
    ];
    counterPatternSource.forEach((counterPattern) => {
      columns.push(`${counterPattern.cd} ${counterPattern.name}`);
      columns.push(`${counterPattern.cd} ${t('CounterPatternSettingPage.individual_setting')}`);
    });
    exportToCsvFile(filename, csvDataList, columns);
    dispatch(appActions.hideAppLoading());
  }

  return (
    <div className="page-wrapper">
      <div className="page-header">
        <StatusHeader />
      </div>
      <div className={`page-main ${isEditMode ? 'reedit-mode-wrapper' : undefined}`}>
        <div className="page-content store-setting-page d-flex flex-column">
          <div>
            <FeatureTitleBar title={t('CounterPatternSettingPage.head_title')}></FeatureTitleBar>
          </div>
          <CounterPatternSettingFormSearch onSearch={handleSearch} onExportCSV={handleExportCSV} />
          <CounterPatternSettingTable
            menuCounterPatternList={sortedData}
            counterPatternSource={counterPatternSource}
            sortBy={sortBy}
            onSortByCol={handleSortByCol}
            onShowCounterPatternMobileOrderDisplayModal={handleShowCounterPatternMobileOrderDisplayModal}
            onShowCounterPatternStoreMobileOrderDisplayModal={handleShowCounterPatternStoreMobileOrderDisplayModal}
          />
        </div>
      </div>
      {showCounterPatternMobileOrderDisplayModal &&
        selectedMobileOrderDisplayModalData &&
        createPortal(
          <CounterPatternMobileOrderDisplayModal
            {...selectedMobileOrderDisplayModalData}
            onClose={handleCloseCounterPatternMobileOrderDisplayModal}
          />,
          document.body
        )}
      {showCounterPatternStoreMobileOrderDisplayModal &&
        selectedStoreMobileOrderDisplayModalData &&
        createPortal(
          <CounterPatternStoreMobileOrderDisplayModal
            {...selectedStoreMobileOrderDisplayModalData}
            onClose={handleCloseCounterPatternStoreMobileOrderDisplayModal}
          />,
          document.body
        )}
    </div>
  );
}

export default CounterPatternSettingPage;
